import { AppError } from '@/utils/api';
import { AuthenticatedHttp } from '../../http/axios';

export const getProductsService = async ({
  search = '',
  status = 'active',
  page = 1,
  limit = 10,
  type,
  contentDeliveries,
  productionType
}) =>
  AuthenticatedHttp.get('/products/', {
    params: {
      page,
      search,
      limit,
      type,
      contentDeliveries,
      productionType,
      ...(status !== 'all' && { status }),
    },
  }).then(({ data: { results, count } }) => ({
    products: results,
    count,
  }));

export const getProductService = async ({ id }) =>
  AuthenticatedHttp.get(`/product/${id}/`).then(({ data: product }) => product);

export const getPublicProductService = async ({ id }) =>
  AuthenticatedHttp.get(`/product/${id}/public/`).then(({ data: product }) => product);

export const getAffiliateProductService = async ({ short_id }) =>
  AuthenticatedHttp.get(`affiliates/products/${short_id}/`).then(({ data: product }) => product);

export const getProductCategoriesService = async () =>
  AuthenticatedHttp.get('/product/categories/').then(({ data: { success, categories } }) => {
    if (!success) {
      throw new Error('Erro ao buscar categorias');
    }
    return categories;
  });

export const createProductOffer = async ({ ...data }) =>
  AuthenticatedHttp.post('/offers/', { ...data })
    .then(({ data: offers }) => offers)
    .catch((error) => {
      throw new AppError(error);
    });

export const createProductService = async ({ ...info }) => {
  const result = await AuthenticatedHttp.post('/products/', {
    ...info,
  })
    .then(({ data: product }) => product)
    .catch((error) => {
      throw new AppError(error);
    });

  if (info.type === 'subscription') {
    const product = await getProductService({ id: result.id });

    await updateProductService({
      ...product,
      id: product.id,
      offers: product.offers.map((offer) => {
        if (offer.default) {
          return {
            ...offer,
            type: 'subscription',
          };
        }
        return offer;
      }),
    });
  }

  return result;
};

export const updateProductService = async ({ id, ...info }) =>
  AuthenticatedHttp.put(`/product/${id}/`, info).then(({ data: product }) => product);

export const updateProductOffers = async ({ id, ...info }) =>
  AuthenticatedHttp.put(`/offers/${id}`, info)
    .then(({ data: offers }) => offers)
    .catch((error) => {
      throw new AppError(error);
    });

export const updateAffiliatePixelsService = async ({ id, tracking_pixels }) =>
  AuthenticatedHttp.put(`/pixels/tracking_pixels/${tracking_pixels.id}/`, tracking_pixels).then(
    ({ data: product }) => product
  );

export const updateProductImageService = async ({ id, image }) => {
  const formData = new FormData();

  formData.append('image', image);

  return AuthenticatedHttp.put(`/product/${id}/image/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then(({ data }) => {
    if (data.detail) return data;
    throw new Error('Erro ao atualizar imagem do produto');
  });
};

export const deleteProductImageService = async ({ id }) =>
  AuthenticatedHttp.delete(`/product/${id}/image/`).then(({ data }) => {
    if (data.detail) return data;
    throw new Error('Erro ao deletar imagem do produto');
  });

export const deleteProductOffer = async ({ id }) => AuthenticatedHttp.delete(`/offers/${id}/`);

export const deleteProductService = async ({ id }) => AuthenticatedHttp.delete(`/product/${id}/`);

export const getPaymentMethods = async () =>
  AuthenticatedHttp.get('/products/payment_methods/')
    .then(({ data }) => data)
    .catch(() => []);

export const getContentDeliveries = async () =>
  AuthenticatedHttp.get('/products/content_delivery/')
    .then(({ data }) => data)
    .catch(() => []);

export const getProductDeliveries = async (productId) =>
  AuthenticatedHttp.get(`/product/${productId}/delivery/`).then(({ data }) => data?.results);

export const createProductDelivery = async ({ id, type, username, password, ...rest }) =>
  AuthenticatedHttp.post(
    `/product/${id}/delivery/`,
    {
      type,
      username,
      password,
      ...rest,
    },
    { withCredentials: true }
  ).then(({ data }) => data);

export const createDeliveryAccessService = async ({ id, authCode, state }) =>
  AuthenticatedHttp.post(
    `/product/delivery_access/${id}/`,
    {
      authCode,
      state,
    },
    { withCredentials: true }
  ).then(({ data }) => data);

export const getDeliveryAccess = async () =>
  AuthenticatedHttp.get('/product/delivery_access/').then(({ data }) => data.results);

export const duplicateProductService = async (id) =>
  AuthenticatedHttp.post(`/product/${id}/duplicate/`).then(({ data }) => data.results);

export const getShowcaseProducts = async (
  search,
  category,
  pageIndex = '',
  filters = {},
  sortBy = null
) => {
  const processedFilters = { ...filters };

  if (
    filters.affiliateCommission &&
    Array.isArray(filters.affiliateCommission) &&
    filters.affiliateCommission.length === 2
  ) {
    processedFilters.affiliateCommission__gte = parseFloat(filters.affiliateCommission[0]).toFixed(
      1
    );
    processedFilters.affiliateCommission__lte = parseFloat(filters.affiliateCommission[1]).toFixed(
      1
    );
    delete processedFilters.affiliateCommission;
  }

  if (filters.price && Array.isArray(filters.price) && filters.price.length === 2) {
    processedFilters.price__gte = filters.price[0];
    processedFilters.price__lte = filters.price[1];
    delete processedFilters.price;
  }

  return AuthenticatedHttp.get('/products/showcase/v2', {
    params: {
      ...(search && { search }),
      ...(category && { category }),
      ...(pageIndex && { page: pageIndex }),
      ...(sortBy && { ordering: `${sortBy.direction === 'desc' ? '-' : ''}${sortBy.field}` }),
      ...processedFilters,
    },
  }).then(({ data }) => data);
};

/**
 * @param {'click' | 'view' | 'affiliate'} type
 * @param {string[]} productIds
 * @returns {Promise<void>}
 */
export const registerMetricsForShowcaseProducts = async (type, productIds) =>
  AuthenticatedHttp.post('/products/showcase/v2/register_events/', {
    type,
    products: productIds,
  });

export const listFunnelConversion = async (productId, startDate, endDate) => {
  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${year}-${month}-${day}`;
  };

  return AuthenticatedHttp.get('/products/funnel', {
    params: {
      product_id: productId,
      ...(startDate && { start_date: formatDate(startDate) }),
      ...(endDate && { end_date: formatDate(endDate) }),
    },
  }).then(({ data }) => data);
};
